import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { route } from 'preact-router';

import { trackPath } from '../../lib/trackPath';

const CARD_WIDTH = 200;
const PHOTO_HEIGHT = 96;
// Photos are toned down so the red track line stays readable over them — bright
// snow and white club logos otherwise wash it out. Tune here, not inline.
const PHOTO_FILTER = 'brightness(0.55) saturate(0.85) contrast(0.95)';
const PHOTO_FILTER_CANCELLED = 'grayscale(0.9) brightness(0.4)';

function shortDate(date) {
    if (!date) {
        return '';
    }
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
        return '';
    }
    return util.formatDate(date, { day: 'numeric', month: 'short', locale: 'nb-NO' }, true);
}

@observer
class PastRaces extends Component {
    constructor(props) {
        super(props);
        this.state = {
            races: [],
            season: {},
        };
    }

    loadAll = async () => {
        const response = await util.fetchApi('/api/races/public/past', { method: 'GET', publish: false }, {});
        if (response?.status === 200 && Array.isArray(response.data?.races)) {
            this.setState({
                races: response.data.races,
                season: response.data.season || {},
            });
        }
    }

    componentDidMount() {
        this.loadAll();
    }

    gotoRace = (e) => {
        const { id } = e.currentTarget.dataset;
        route(`/race/${id}`);
    }

    gotoAllRaces = () => {
        route('/race?showAll=1');
    }

    render() {
        const { races, season = {} } = this.state;
        const { classNames = '' } = this.props;

        if (races.length < 1) {
            return (<></>);
        }

        const seasonSpan = [shortDate(season.firstStart), shortDate(season.lastEnd)]
            .filter(e => e.length > 0)
            .join('–');
        const seasonYear = season.lastEnd ? new Date(season.lastEnd).getFullYear() : 0;

        return (<div class={`w-100 ${classNames}`}>
            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between align-items-baseline mb-1'>
                <span>
                    <i class='fa-duotone fa-clock-rotate-left me-2' />
                    <Text id='race.past-races'>Last season</Text>
                </span>
                <span
                    class='text-primary'
                    style='cursor: pointer; font-size: 0.9rem;'
                    onClick={this.gotoAllRaces}
                >
                    <Text id='race.past-races-see-all'>See all</Text> <i class='fa-solid fa-arrow-right ms-1' />
                </span>
            </div>

            {seasonSpan.length > 0 && <div class='px-3 text-body-secondary mb-2' style='font-size: 0.75rem;'>
                {seasonSpan}{seasonYear > 0 ? ` ${seasonYear}` : ''}
                {season.cancelled > 0 && <span class='text-danger ms-2'>
                    · {season.cancelled} <Text id='race.season-cancelled'>cancelled</Text>
                </span>}
            </div>}

            <div class='d-flex flex-row flex-wrap px-3 mb-3' style='gap: 1.25rem;'>
                {season.races > 0 && <div class='d-flex flex-column'>
                    <span class='fs-5 fw-semibold text-primary lh-1'>{util.format(season.races, 0)}</span>
                    <small class='text-body-secondary text-uppercase' style='font-size: 0.65rem; letter-spacing: 0.04em;'>
                        <Text id='race.season-races'>races</Text>
                    </small>
                </div>}
                {season.contestants > 0 && <div class='d-flex flex-column'>
                    <span class='fs-5 fw-semibold text-primary lh-1'>{util.format(season.contestants, 0)}</span>
                    <small class='text-body-secondary text-uppercase' style='font-size: 0.65rem; letter-spacing: 0.04em;'>
                        <Text id='race.season-teams'>teams</Text>
                    </small>
                </div>}
                {season.countries > 0 && <div class='d-flex flex-column'>
                    <span class='fs-5 fw-semibold text-primary lh-1'>{util.format(season.countries, 0)}</span>
                    <small class='text-body-secondary text-uppercase' style='font-size: 0.65rem; letter-spacing: 0.04em;'>
                        <Text id='race.season-countries'>nations</Text>
                    </small>
                </div>}
                {season.raceDays > 0 && <div class='d-flex flex-column'>
                    <span class='fs-5 fw-semibold text-primary lh-1'>{util.format(season.raceDays, 0)}</span>
                    <small class='text-body-secondary text-uppercase' style='font-size: 0.65rem; letter-spacing: 0.04em;'>
                        <Text id='race.season-race-days'>race days</Text>
                    </small>
                </div>}
                {season.longestRaceKm > 0 && <div class='d-flex flex-column'>
                    <span class='fs-5 fw-semibold text-primary lh-1'>{util.format(season.longestRaceKm, 0)}</span>
                    <small class='text-body-secondary text-uppercase' style='font-size: 0.65rem; letter-spacing: 0.04em;'>
                        <Text id='race.season-longest-km'>km longest race</Text>
                    </small>
                </div>}
                {season.trackingKm > 0 && <div class='d-flex flex-column'>
                    <span class='fs-5 fw-semibold text-primary lh-1'>{util.format(season.trackingKm, 0)}</span>
                    <small class='text-body-secondary text-uppercase' style='font-size: 0.65rem; letter-spacing: 0.04em;'>
                        <Text id='race.season-tracked-km'>km tracked</Text>
                    </small>
                </div>}
            </div>

            {season.countryFlags?.length > 0 && <div class='px-3 mb-3' style='font-size: 1.1rem; line-height: 1.4;'>
                {season.countryFlags.join(' ')}
            </div>}

            <div
                class='d-flex flex-row flex-nowrap px-3 pb-2'
                style='overflow-x: auto; scroll-snap-type: x mandatory; gap: 12px; -webkit-overflow-scrolling: touch; max-width: 100%;'
            >
                {races.map(race => {
                    const path = trackPath(race.trackOutline, CARD_WIDTH, PHOTO_HEIGHT, 10);
                    const year = race.startDate ? new Date(race.startDate).getFullYear() : 0;
                    const startDateText = shortDate(race.startDate);
                    const endDateText = shortDate(race.endDate);
                    let dateRangeText = '';
                    if (startDateText.length > 0 && endDateText.length > 0) {
                        dateRangeText = `${startDateText}–${endDateText}`;
                    } else if (startDateText.length > 0) {
                        dateRangeText = startDateText;
                    } else if (endDateText.length > 0) {
                        dateRangeText = endDateText;
                    }
                    return (<div
                        key={`past-race-${race.id}`}
                        class='rounded-3 overflow-hidden flex-shrink-0 bg-body-secondary'
                        style={`width: ${CARD_WIDTH}px; scroll-snap-align: start; cursor: pointer;`}
                        data-id={race.id}
                        onClick={this.gotoRace}
                    >
                        <div class='position-relative w-100 overflow-hidden' style={`height: ${PHOTO_HEIGHT}px;`}>
                            {/* The photo lives in its own layer so toning it down never
                                dims the track drawn on top of it. Busy white-logo images
                                otherwise swallow the red line completely. */}
                            <div
                                class='position-absolute top-0 start-0 w-100 h-100'
                                style={`${race.image
                                    ? `background-image: url("${race.image}"); background-size: cover; background-position: center;`
                                    : 'background: repeating-linear-gradient(135deg, var(--bs-secondary-bg) 0px, var(--bs-secondary-bg) 8px, var(--bs-tertiary-bg) 8px, var(--bs-tertiary-bg) 16px);'} filter: ${race.isCancelled ? PHOTO_FILTER_CANCELLED : PHOTO_FILTER};`}
                            />
                            {!race.image && <div class='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-body-tertiary' style='font-size: 1.5rem;'>
                                <i class='fa-duotone fa-flag-checkered' />
                            </div>}
                            {race.image && path.length > 0 && <svg
                                class='position-absolute top-0 start-0 w-100 h-100'
                                viewBox={`0 0 ${CARD_WIDTH} ${PHOTO_HEIGHT}`}
                                preserveAspectRatio='none'
                            >
                                {/* White halo first, red line on top — the track has to read
                                    against both bright snow and dark forest photos. */}
                                <path
                                    d={path}
                                    fill='none'
                                    stroke='#ffffff'
                                    stroke-width='4.5'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                    opacity='0.95'
                                />
                                <path
                                    d={path}
                                    fill='none'
                                    stroke='#dc3545'
                                    stroke-width='2'
                                    stroke-linecap='round'
                                    stroke-linejoin='round'
                                />
                            </svg>}
                            {year > 0 && <span
                                class='position-absolute badge rounded-pill text-white'
                                style='top: 6px; left: 6px; background-color: rgba(0, 0, 0, 0.65); font-size: 0.6rem;'
                            >
                                {year}
                            </span>}
                            {race.isCancelled && <span
                                class='position-absolute badge rounded-pill bg-danger text-white text-uppercase'
                                style='top: 6px; right: 6px; font-size: 0.6rem; letter-spacing: 0.04em;'
                            >
                                <i class='fa-solid fa-ban me-1' /><Text id='race.cancelled'>Cancelled</Text>
                            </span>}
                        </div>
                        <div class='px-2 py-2'>
                            <div class='text-body fw-semibold text-truncate' style='font-size: 0.8rem;'>{race.title}</div>
                            <div class='text-body-secondary text-truncate' style='font-size: 0.68rem;'>
                                {dateRangeText.length > 0 && <span>{dateRangeText}</span>}
                                {race.distance > 0 && <span> · {util.format(race.distance, 0)} km</span>}
                                {race.totalContestants > 0 && <span> · {race.totalContestants} <Text id='race.spann'>teams</Text></span>}
                            </div>
                        </div>
                    </div>);
                })}
            </div>
        </div>);
    }
}

export default PastRaces;
